/**
 * Expense Form Page
 *
 * Form for recording project expenses with receipt upload
 * Includes budget check warnings at 80% and 100%
 *
 * @module pages/expenses/ExpenseForm
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Modal,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import {
  recordExpense,
  getExpense,
  updateExpense,
  previewExpenseBudgetImpact,
} from '../../services/expenseService';
import { getActiveSuppliers } from '../../services/supplierService';
import { listProjects } from '../../services/projectService';
import { listTasks } from '../../services/taskService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EXPENSE_CATEGORY, FILE_UPLOAD } from '../../utils/constants';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
// #765 — los umbrales 80/100, nombrados una sola vez en el árbol web.
import { BUDGET_WARNING_THRESHOLD, BUDGET_CRITICAL_THRESHOLD } from '../../utils/budgetStatus';
import { useAuth } from '../../contexts/AuthContext';
import FileUpload from '../../components/common/FileUpload';
import DashboardLayout from '../../components/layout/DashboardLayout';

/**
 * Form validation schema
 */
const expenseSchema = yup.object().shape({
  project_id: yup.string().required('Debe seleccionar un proyecto'),
  task_id: yup.string().nullable(),
  category: yup.string().required('La categoría es requerida'),
  amount: yup
    .number()
    .required('El monto es requerido')
    .positive('El monto debe ser positivo')
    .max(1000000000, 'Monto demasiado grande'),
  vendor: yup.string().max(200, 'Nombre de proveedor demasiado largo'),
  expense_date: yup.date().required('La fecha es requerida'),
  notes: yup.string().max(1000, 'Las notas no pueden exceder 1000 caracteres'),
});

/**
 * ExpenseForm component
 */
const ExpenseForm = () => {
  const navigate = useNavigate();
  const { id: expenseId } = useParams();
  const [searchParams] = useSearchParams();
  const projectIdParam = searchParams.get('projectId');
  const taskIdParam = searchParams.get('taskId');
  const { isAdmin, isTechnician, isSupervisor } = useAuth();

  const isEditMode = !!expenseId;
  const queryClient = useQueryClient();

  const [receiptFile, setReceiptFile] = useState(null);
  const [existingReceiptUrl, setExistingReceiptUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBudgetWarning, setShowBudgetWarning] = useState(false);
  const [budgetAlert, setBudgetAlert] = useState(null);
  // Ref y no state a propósito. `handleProceedWithWarning` re-invoca el
  // `onSubmit` del render ACTUAL, que cierra sobre el valor viejo del state:
  // con `useState` el flag seguía en `false` dentro de esa llamada y el modal
  // se volvía a abrir en bucle. Un ref se lee en el momento de la llamada.
  const proceedDespiteWarningRef = useRef(false);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const supplierRef = useRef(null);

  // Fetch projects
  const { data: projectsData, isLoading: loadingProjects } = useQuery({
    queryKey: ['projects', { pageSize: 100 }],
    queryFn: () => listProjects({ pageSize: 100 }),
  });

  const projects = projectsData?.data || [];

  // React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(expenseSchema),
    defaultValues: {
      project_id: projectIdParam || '',
      task_id: taskIdParam || '',
      category: EXPENSE_CATEGORY.MATERIALS,
      amount: '',
      vendor: '',
      expense_date: new Date().toISOString().split('T')[0],
      notes: '',
    },
  });

  // Fetch existing expense for edit mode
  const { data: expenseData, isLoading: loadingExpense } = useQuery({
    queryKey: ['expense', expenseId],
    queryFn: () => getExpense(expenseId),
    enabled: isEditMode,
    staleTime: 0, // Always refetch to ensure fresh data
    gcTime: 0, // Don't cache (formerly cacheTime)
  });

  // Populate form when expense data is loaded
  useEffect(() => {
    if (expenseData?.data && isEditMode) {
      const expense = expenseData.data;
      reset({
        project_id: expense.project_id || '',
        task_id: expense.task_id || '',
        category: expense.category || EXPENSE_CATEGORY.MATERIALS,
        amount: expense.amount || '',
        vendor: expense.vendor || '',
        expense_date: expense.expense_date || new Date().toISOString().split('T')[0],
        notes: expense.notes || '',
      });
      if (expense.receipt_url) {
        setExistingReceiptUrl(expense.receipt_url);
      }
    }
  }, [expenseData, isEditMode, reset]);

  const selectedProjectId = watch('project_id');

  // Fetch tasks for selected project
  const { data: tasksData, isLoading: loadingTasks } = useQuery({
    queryKey: ['tasks', { projectId: selectedProjectId }],
    queryFn: () => listTasks({ projectId: selectedProjectId, pageSize: 100 }),
    enabled: !!selectedProjectId,
  });

  const tasks = tasksData?.data || [];

  // Fetch active suppliers for vendor combobox
  const { data: activeSuppliers = [] } = useQuery({
    queryKey: ['activeSuppliers'],
    queryFn: getActiveSuppliers,
    select: (res) => res.data ?? [],
    staleTime: 5 * 60 * 1000,
  });

  // Close supplier dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (supplierRef.current && !supplierRef.current.contains(e.target)) {
        setShowSupplierDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Check permissions
   */
  useEffect(() => {
    // El supervisor registra gastos: `supervisor_create_expenses (INSERT)` existe
    // en produccion y la matriz dice "Registrar gasto contra proyecto: ✅".
    // Este formulario lo echaba, asi que la RLS le concedia algo que la pantalla
    // le negaba antes de dejarlo intentar.
    //
    // Editar sigue siendo de admin: la ruta `:id/editar` en App.js no lo incluye,
    // y el techo `expenses_ceiling_update` lo respalda del lado de la base.
    if (!isAdmin && !isTechnician && !isSupervisor) {
      toast.error('No tienes permisos para registrar gastos');
      navigate('/gastos');
    }
  }, [isAdmin, isTechnician, isSupervisor, navigate]);

  /**
   * Handle form submission
   */
  const onSubmit = async (formData) => {
    try {
      setLoading(true);

      // Clean up empty task_id (convert empty string to null)
      const cleanedData = {
        ...formData,
        task_id: formData.task_id || null,
      };

      let result;

      if (isEditMode) {
        // Update existing expense
        result = await updateExpense(expenseId, cleanedData, receiptFile);

        if (result.error) throw result.error;

        // Invalidate cache to ensure fresh data on next edit
        await queryClient.invalidateQueries({ queryKey: ['expense', expenseId] });
        await queryClient.invalidateQueries({ queryKey: ['expenses'] });

        toast.success('Gasto actualizado exitosamente');
        navigate('/gastos');
      } else {
        // El chequeo de presupuesto va ANTES del INSERT.
        //
        // Antes se hacía al revés: `recordExpense` escribía la fila y recién
        // entonces se abría el modal. Consecuencias reales, las dos malas:
        // "Cancelar" no deshacía nada (el gasto ya estaba guardado) y
        // "Continuar" disparaba un SEGUNDO `recordExpense` con los mismos
        // datos, o sea un gasto duplicado. El texto de ayuda de abajo ya
        // prometía la alerta "antes de guardarlo"; ahora es cierto.
        if (!proceedDespiteWarningRef.current) {
          const { data: projected } = await previewExpenseBudgetImpact(
            cleanedData.project_id,
            cleanedData.amount
          );

          if (projected && projected.variancePercentage >= BUDGET_WARNING_THRESHOLD) {
            setBudgetAlert(projected);
            setShowBudgetWarning(true);
            setLoading(false);
            return; // nada escrito todavía
          }
        }

        // Record new expense
        result = await recordExpense(cleanedData, receiptFile);

        if (result.error) throw result.error;

        proceedDespiteWarningRef.current = false;
        toast.success('Gasto registrado exitosamente');
        navigate('/gastos');
      }
    } catch (error) {
      console.error('Record expense error:', error);
      toast.error(isEditMode ? 'Error al actualizar el gasto' : 'Error al registrar el gasto');
      setLoading(false);
    }
  };

  /**
   * Handle proceed with warning
   */
  const handleProceedWithWarning = async () => {
    proceedDespiteWarningRef.current = true;
    setShowBudgetWarning(false);
    // Re-submit form. Esta vez el ref ya está en true, así que se saltea el
    // preview y escribe UNA sola fila.
    handleSubmit(onSubmit)();
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    navigate('/gastos');
  };

  const isCritical = budgetAlert?.variancePercentage >= BUDGET_CRITICAL_THRESHOLD;

  // Loading state for edit mode
  if (isEditMode && loadingExpense) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Cargando gasto...</span>
            </div>
            <p className="text-muted">Cargando datos del gasto...</p>
          </div>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card>
              <Card.Header className="bg-primary text-white">
                <h4 className="mb-0">
                  <i className={`bi ${isEditMode ? 'bi-pencil' : 'bi-receipt'} me-2`}></i>
                  {isEditMode ? 'Editar Gasto' : 'Registrar Gasto'}
                </h4>
              </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    {/* Project */}
                    <Form.Group className="mb-3" controlId="project_id">
                      <Form.Label>
                        Proyecto <span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="project_id"
                        control={control}
                        render={({ field }) => (
                          <Form.Select
                            {...field}
                            isInvalid={!!errors.project_id}
                            disabled={isSubmitting || loadingProjects}
                            data-testid="expense-form-project-select"
                          >
                            <option value="">Seleccionar proyecto...</option>
                            {projects.map((project) => (
                              <option key={project.id} value={project.id}>
                                {project.project_number} - {project.title}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      />
                      {errors.project_id && (
                        <Form.Control.Feedback type="invalid">
                          {errors.project_id.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    {/* Task (Optional) */}
                    <Form.Group className="mb-3" controlId="task_id">
                      <Form.Label>Tarea (Opcional)</Form.Label>
                      <Controller
                        name="task_id"
                        control={control}
                        render={({ field }) => (
                          <Form.Select
                            {...field}
                            isInvalid={!!errors.task_id}
                            disabled={isSubmitting || loadingTasks || !selectedProjectId}
                            data-testid="expense-form-task-select"
                          >
                            <option value="">Gasto a nivel de proyecto</option>
                            {tasks.map((task) => (
                              <option key={task.id} value={task.id}>
                                {task.task_number} - {task.title}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      />
                      {errors.task_id && (
                        <Form.Control.Feedback type="invalid">
                          {errors.task_id.message}
                        </Form.Control.Feedback>
                      )}
                      <Form.Text className="text-muted">
                        Asignar este gasto a una tarea específica (opcional)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    {/* Category */}
                    <Form.Group className="mb-3" controlId="category">
                      <Form.Label>
                        Categoría
                      </Form.Label>
                      <Controller
                        name="category"
                        control={control}
                        render={({ field }) => (
                          <Form.Select
                            {...field}
                            isInvalid={!!errors.category}
                            disabled={isSubmitting}
                            data-testid="expense-form-category-select"
                          >
                            <option value={EXPENSE_CATEGORY.MATERIALS}>Materiales</option>
                            <option value={EXPENSE_CATEGORY.TOOLS}>Herramientas</option>
                            <option value={EXPENSE_CATEGORY.LABOR}>Mano de Obra</option>
                            <option value={EXPENSE_CATEGORY.TRANSPORTATION}>Transporte</option>
                            <option value={EXPENSE_CATEGORY.OTHER}>Otros</option>
                          </Form.Select>
                        )}
                      />
                      {errors.category && (
                        <Form.Control.Feedback type="invalid">
                          {errors.category.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    {/* Amount */}
                    <Form.Group className="mb-3" controlId="amount">
                      <Form.Label>
                        {/* #617: sin "(USD)". `expenses` no tiene columna de moneda. */}
                        Monto <span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            isInvalid={!!errors.amount}
                            disabled={isSubmitting}
                            data-testid="expense-form-amount-input"
                          />
                        )}
                      />
                      {errors.amount && (
                        <Form.Control.Feedback type="invalid">
                          {errors.amount.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    {/* Expense Date */}
                    <Form.Group className="mb-3" controlId="expense_date">
                      <Form.Label>
                        Fecha del Gasto
                      </Form.Label>
                      <Controller
                        name="expense_date"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="date"
                            isInvalid={!!errors.expense_date}
                            disabled={isSubmitting}
                          />
                        )}
                      />
                      {errors.expense_date && (
                        <Form.Control.Feedback type="invalid">
                          {errors.expense_date.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {/* Vendor combobox */}
                <Form.Group className="mb-3" controlId="vendor">
                  <Form.Label>Proveedor / Vendedor</Form.Label>
                  <Controller
                    name="vendor"
                    control={control}
                    render={({ field }) => {
                      const filtered = activeSuppliers.filter((s) =>
                        s.name.toLowerCase().includes((field.value || '').toLowerCase())
                      );
                      return (
                        <div ref={supplierRef} style={{ position: 'relative' }}>
                          <Form.Control
                            type="text"
                            placeholder="Nombre del proveedor..."
                            value={field.value || ''}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                              setShowSupplierDropdown(true);
                            }}
                            onFocus={() => setShowSupplierDropdown(true)}
                            isInvalid={!!errors.vendor}
                            disabled={isSubmitting}
                            autoComplete="off"
                          />
                          {showSupplierDropdown && filtered.length > 0 && (
                            <ul
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                zIndex: 1050,
                                maxHeight: '200px',
                                overflowY: 'auto',
                                margin: 0,
                                padding: 0,
                                listStyle: 'none',
                                background: '#fff',
                                border: '1px solid #ced4da',
                                borderRadius: '0 0 4px 4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              }}
                            >
                              {filtered.map((s) => (
                                <li
                                  key={s.id}
                                  style={{ padding: '8px 12px', cursor: 'pointer' }}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    field.onChange(s.name);
                                    setShowSupplierDropdown(false);
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f8f9fa')}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                                >
                                  {s.name}
                                </li>
                              ))}
                            </ul>
                          )}
                          {errors.vendor && (
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                              {errors.vendor.message}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Form.Text className="text-muted">Opcional</Form.Text>
                </Form.Group>

                {/* Receipt Upload */}
                <Form.Group className="mb-3">
                  <Form.Label>Recibo / Factura</Form.Label>
                  {isEditMode && existingReceiptUrl && !receiptFile && (
                    <div className="mb-2 p-2 border rounded bg-light">
                      <div className="d-flex align-items-center gap-2">
                        <img
                          src={existingReceiptUrl}
                          alt="Recibo actual"
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #dee2e6',
                          }}
                        />
                        <div className="flex-grow-1">
                          <small className="text-muted d-block">Recibo actual</small>
                          <a
                            href={existingReceiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="small"
                          >
                            Ver imagen completa
                          </a>
                        </div>
                      </div>
                      <small className="text-muted mt-2 d-block">
                        Sube una nueva imagen para reemplazar el recibo actual.
                      </small>
                    </div>
                  )}
                  <FileUpload
                    onFilesSelected={(files) => setReceiptFile(files?.[0] || null)}
                    acceptedTypes={FILE_UPLOAD.ACCEPTED_TYPES.RECEIPTS}
                    maxSize={FILE_UPLOAD.MAX_SIZE}
                    maxFiles={1}
                    multiple={false}
                  />
                  <Form.Text className="text-muted">
                    {isEditMode && existingReceiptUrl
                      ? 'Opcional. Sube una nueva imagen para reemplazar el recibo actual.'
                      : 'Opcional. Formatos: JPG, PNG, PDF. Máximo 10MB.'}
                  </Form.Text>
                </Form.Group>

                {/* Notes */}
                <Form.Group className="mb-4" controlId="notes">
                  <Form.Label>Notas</Form.Label>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        as="textarea"
                        rows={3}
                        placeholder="Notas adicionales sobre el gasto..."
                        isInvalid={!!errors.notes}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                  {errors.notes && (
                    <Form.Control.Feedback type="invalid">
                      {errors.notes.message}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">Opcional. Máximo 1000 caracteres.</Form.Text>
                </Form.Group>

                {/* Action Buttons */}
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isSubmitting || loading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || loading || loadingExpense}
                    data-testid="expense-form-submit-button"
                  >
                    {isSubmitting || loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {isEditMode ? 'Actualizando...' : 'Registrando...'}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-1"></i>
                        {isEditMode ? 'Actualizar Gasto' : 'Registrar Gasto'}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Help Text */}
          <Alert variant="info" className="mt-3">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Nota:</strong> Si el gasto excede el 80% del presupuesto, recibirás una alerta antes de guardarlo.
          </Alert>
        </Col>
      </Row>

      {/* Budget Warning Modal */}
      <Modal
        show={showBudgetWarning}
        onHide={() => setShowBudgetWarning(false)}
        centered
        backdrop="static"
        data-testid="expense-budget-warning-modal"
      >
        <Modal.Header className={`text-white ${isCritical ? 'bg-danger' : 'bg-warning'}`}>
          <Modal.Title>
            <i className={`bi ${isCritical ? 'bi-exclamation-octagon' : 'bi-exclamation-triangle'} me-2`}></i>
            {isCritical ? 'ALERTA CRÍTICA: Presupuesto Excedido' : 'ADVERTENCIA: Presupuesto Próximo al Límite'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {budgetAlert && (
            <>
              <p className="mb-3">
                {isCritical
                  ? 'Este gasto excederá el presupuesto del proyecto.'
                  : 'Este gasto consumirá más del 80% del presupuesto del proyecto.'}
              </p>
              <div className="bg-light p-3 rounded">
                <div className="d-flex justify-content-between mb-2">
                  <span>Presupuesto:</span>
                  <strong>{formatCurrency(budgetAlert.quotedAmount)}</strong>
                </div>
                {/* `spentAmount` acá ya viene PROYECTADO (lo gastado + este
                    gasto todavía sin registrar), porque el aviso se calcula
                    antes de escribir. La etiqueta lo dice para que el número
                    no se lea como un saldo ya ocurrido. */}
                <div className="d-flex justify-content-between mb-2">
                  <span>Gastado + este gasto:</span>
                  <strong>{formatCurrency(budgetAlert.spentAmount)}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Uso del presupuesto:</span>
                  <strong className={isCritical ? 'text-danger' : 'text-warning'}>
                    {formatPercentage(budgetAlert.variancePercentage, 1)}
                  </strong>
                </div>
              </div>
              <p className="mt-3 mb-0">
                ¿Deseas continuar con el registro de este gasto?
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              // Ahora "Cancelar" cancela de verdad: el gasto no se escribió.
              proceedDespiteWarningRef.current = false;
              setShowBudgetWarning(false);
              setLoading(false);
            }}
            data-testid="expense-budget-cancel-button"
          >
            Cancelar
          </Button>
          <Button variant={isCritical ? 'danger' : 'warning'} onClick={handleProceedWithWarning} data-testid="expense-budget-proceed-button">
            Continuar de Todos Modos
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
    </DashboardLayout>
  );
};

export default ExpenseForm;
